// ─────────────────────────────────────────────────────────────────────────────
// C2 — los dos Top-10 del tab Diario, con las OCHO columnas de la maqueta:
//   # | Nombre | Área | OU | Fundamental | Recos | Cross-OU | Tipo
//
// Eran una `RankedList` de tres datos (#, nombre, conteo). Las otras cinco no estaban ocultas: no
// viajaban. Ahora las manda AnalyticsService (AS #25), donde se decidieron midiendo:
//   · «Tipo» sale de `IsManager` de la PERSONA, no del `EsLider` del recibo — ese describe el
//     reconocimiento, y una colaboradora reconocida por su jefa saldría como «Líderes».
//   · «Cross-OU» es «alguno cruzó», y es `bool?`: `false` afirma que no cruza, `null` dice que no
//     se sabe porque la OU del otro extremo no se resolvió.
// ─────────────────────────────────────────────────────────────────────────────
import { useTranslation } from 'react-i18next';
import type { RankedEntryDto } from '@/api/recognition';

/**
 * Cómo se pinta el cruce de OU. Aparte del render para poder probarlo: son TRES estados en un campo
 * que parece booleano, y colapsarlos a dos es justo el error que `bool?` existe para evitar.
 */
export function textoCrossOu(valor: boolean | null | undefined): 'si' | 'no' | 'desconocido' {
  if (valor === true) return 'si';
  if (valor === false) return 'no';
  return 'desconocido';
}

export function TopPersonasTable({
  entries,
  colCount,
  desde = 0,
}: Readonly<{
  entries: readonly RankedEntryDto[];
  colCount: string;
  /**
   * Cuántas filas hay ANTES de `entries` (páginas anteriores). 2026-09-29 (documento de Adriana,
   * bug) — sin esto la columna «#» volvía a empezar en 1 en cada página del paginador.
   */
  desde?: number;
}>) {
  const { t } = useTranslation('recognition');

  if (entries.length === 0) return <div className="rr-state">{t('reports.common.empty')}</div>;

  return (
    <div className="rr-table-wrap">
      <table className="rr-table">
        <thead>
          <tr>
            <th className="rr-num">#</th>
            <th>{t('reports.daily.colName')}</th>
            {/* La columna trae el DEPARTAMENTO de la persona, no un «área»: AnalyticsService la
                resuelve con `Area: user?.Department` (`TopPersonasEnriquecer`). El campo del DTO
                sigue llamándose `area`. */}
            <th>{t('reports.daily.colDepto')}</th>
            <th>{t('reports.daily.colOu')}</th>
            <th>{t('reports.daily.colFundamental')}</th>
            <th className="rr-num">{colCount}</th>
            <th>{t('reports.daily.colCrossOu')}</th>
            <th>{t('reports.daily.colType')}</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e, i) => (
            <tr key={e.id || i}>
              <td className="rr-num">{desde + i + 1}</td>
              <td>
                {e.suprimido
                  ? <span className="rr-muted">{t('reports.common.masked')}</span>
                  : e.nombre}
              </td>
              {/* Las cuatro derivadas heredan la supresión: si la persona está tapada por anonimato,
                  su área y su OU la reidentificarían igual que su nombre. */}
              <td>{e.suprimido ? '—' : (e.area ?? '—')}</td>
              <td>{e.suprimido ? '—' : (e.ouName ?? '—')}</td>
              <td>{e.suprimido ? '—' : (e.fundamentalName ?? '—')}</td>
              <td className="rr-num">{e.suprimido ? '—' : e.count}</td>
              <td>
                {e.suprimido
                  ? '—'
                  : t(`reports.daily.crossOu.${textoCrossOu(e.crossOu)}`)}
              </td>
              <td>{e.suprimido ? '—' : (e.tipo ?? '—')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
